import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { OwnerAgentWindowView } from '@api/automation/contracts';
import { agentWindowMaxOptions, agentWindowOptions, approveAgentWindow, closeAgentWindow, getAgentWindowSettings, getAgentWindows,
  setAgentWindowMax } from '../lib/api';
import { signWithKey } from '../lib/webauthn';
import { absolute } from '../lib/time';
import { useScope } from '../scope/scope';
import { Button } from '../ui/Button';
import { useToast } from '../ui/toast';

const DURATIONS = [15, 30, 60, 120, 240, 480, 720, 1440];
export const duration = (minutes: number) => minutes < 60 ? `${minutes} minutes` : minutes === 60 ? '1 hour' : `${minutes / 60} hours`;
const select = 'h-8 rounded-lg border border-line bg-panel px-2 text-[13px]';

/**
 * Agent windows (WRMM-397 PR 2): one key touch lets an agent run scripts and runbooks on exactly the devices shown, with no
 * further touch, until the window ends. The issuing owner chooses how long and whether it may restart them; any owner can
 * end one at once. Each device checks the window's approval itself.
 */
export function AgentWindows() {
  const { mspId } = useScope();
  const client = useQueryClient();
  const toast = useToast();
  const key = ['scope', mspId, 'agent-windows'];
  const list = useQuery({ queryKey: key, queryFn: ({ signal }) => getAgentWindows(mspId, signal), refetchInterval: 15_000 });
  const settings = useQuery({ queryKey: [...key, 'settings'], queryFn: ({ signal }) => getAgentWindowSettings(mspId, signal) });
  const refresh = () => void client.invalidateQueries({ queryKey: key });
  const approve = useMutation({
    mutationFn: async ({ id, minutes, allowRestart }: { id: string; minutes: number; allowRestart: boolean }) =>
      approveAgentWindow(mspId, id, await signWithKey(await agentWindowOptions(mspId, id, { minutes, allowRestart }))),
    onSuccess: w => { refresh(); toast({ title: 'Window opened', body: `Until ${w.expiresAt ? absolute(w.expiresAt) : 'its end'}. You can end it here sooner.` }); },
    onError: () => toast({ tone: 'warning', title: 'The window wasn’t opened', body: 'Only the owner who issued this agent’s credential can approve it. Touch your key when asked; an expired request needs a new one.' }),
  });
  const close = useMutation({
    mutationFn: ({ id, stopRunning }: { id: string; stopRunning: boolean }) => closeAgentWindow(mspId, id, stopRunning),
    onSuccess: rows => client.setQueryData(key, rows),
    onError: () => toast({ tone: 'warning', title: 'The window couldn’t be ended', body: 'Refresh and try again.' }),
  });
  const max = useMutation({
    mutationFn: async (minutes: number) => setAgentWindowMax(mspId, minutes, await signWithKey(await agentWindowMaxOptions(mspId, minutes))),
    onSuccess: () => { void client.invalidateQueries({ queryKey: [...key, 'settings'] }); toast({ title: 'Longest window changed' }); },
    onError: () => toast({ tone: 'warning', title: 'The longest window wasn’t changed', body: 'Touch your key when asked.' }),
  });
  const longest = settings.data?.maxMinutes ?? 720;
  const busy = approve.isPending || close.isPending;
  return <section className="space-y-2" aria-label="Agent windows">
    <h3 className="text-[14px] font-medium">Agent windows</h3>
    <p className="text-[12.5px] text-ink-3">An agent with “Ask to run scripts and runbooks” can ask for a window on the computers you named. One touch of your key lets it run scripts and runbooks on exactly those computers, as SYSTEM or root, without another touch, until the window ends. Each computer checks the window itself.</p>
    {settings.data && <label className="flex items-center gap-2 text-[12.5px] text-ink-2">Longest window
      <select aria-label="Longest window" className={select} value={settings.data.maxMinutes} disabled={max.isPending}
        onChange={e => max.mutate(Number(e.target.value))}>
        {DURATIONS.filter(m => m >= 60 && m <= settings.data!.ceilingMinutes).map(m => <option key={m} value={m}>{duration(m)}</option>)}
      </select>{max.isPending && 'Touch your key…'}</label>}
    {list.isPending && <p className="text-[13px] text-ink-3">Loading windows…</p>}
    {list.isError && <p role="alert" className="text-[13px] text-ink-3">Agent windows couldn’t be loaded.</p>}
    {list.data?.length === 0 && <p className="text-[13px] text-ink-3">No agent windows in the last day.</p>}
    <ul className="divide-y divide-line rounded-lg border border-line">
      {list.data?.map(w => <WindowRow key={w.id} w={w} longest={longest} busy={busy}
        onApprove={(minutes, allowRestart) => approve.mutate({ id: w.id, minutes, allowRestart })}
        onClose={stopRunning => close.mutate({ id: w.id, stopRunning })} approving={approve.isPending} />)}
    </ul>
  </section>;
}

function WindowRow({ w, longest, busy, approving, onApprove, onClose }: { w: OwnerAgentWindowView; longest: number; busy: boolean; approving: boolean;
  onApprove: (minutes: number, allowRestart: boolean) => void; onClose: (stopRunning: boolean) => void }) {
  const choices = DURATIONS.filter(m => m <= longest);
  const [minutes, setMinutes] = useState(choices.filter(m => m <= w.requestedMinutes).at(-1) ?? choices[0]!);
  const [restart, setRestart] = useState(w.requestedRestart);
  const label = { pending: 'waiting for approval', lapsed: 'not approved in time', active: 'open', expired: 'ended', closed: 'ended early' }[w.status];
  return <li className="space-y-1.5 px-3 py-2 text-[13px]">
    <p className="font-medium">{w.agentName} · {label} · {w.devices.length} {w.devices.length === 1 ? 'computer' : 'computers'}</p>
    <p className="text-ink-2">{w.reason}</p>
    <p className="text-[12.5px] text-ink-3">{w.status === 'active' && w.expiresAt ? `Open until ${absolute(w.expiresAt)} · ${w.allowRestart ? 'may restart them' : 'no restarts'} · ${w.jobs} jobs`
      : w.status === 'pending' ? `Asked for ${duration(w.requestedMinutes)}${w.requestedRestart ? ', with restarts' : ''} · request expires ${absolute(w.requestExpiresAt)}`
      : `${w.jobs} jobs`}</p>
    <ul className="space-y-0.5">
      {w.devices.map(d => <li key={d.deviceId}>{d.hostname ?? 'Removed computer'} · {d.deviceId.slice(0, 8)}
        <span className="text-ink-3"> {[d.customerName, d.siteName].filter(Boolean).join(' / ')}</span></li>)}
    </ul>
    {w.status === 'pending' && w.canApprove && <div className="flex flex-wrap items-center gap-2">
      <select aria-label="How long" className={select} value={minutes} onChange={e => setMinutes(Number(e.target.value))}>
        {choices.map(m => <option key={m} value={m}>{duration(m)}</option>)}
      </select>
      <label className="flex items-center gap-1.5"><input type="checkbox" checked={restart} onChange={e => setRestart(e.target.checked)} /> May restart them</label>
      <Button size="sm" disabled={busy} onClick={() => onApprove(minutes, restart)}>{approving ? 'Touch your key…' : `Open for ${duration(minutes)}`}</Button>
    </div>}
    {w.status === 'pending' && w.canApprove && <p className="text-[12.5px] text-ink-3">Without “May restart them”, scripts and runbooks that would
      restart or shut down a computer are refused, by the server and by each computer. That catches mistakes; a script written to hide a restart
      can still restart, as SYSTEM or root can do anything. Approve only agents you trust with these computers.</p>}
    {w.status === 'pending' && !w.canApprove && <p className="text-ink-3">Only the owner who issued this agent’s credential can approve it.</p>}
    {w.status === 'pending' && <Button size="sm" variant="secondary" disabled={busy} onClick={() => onClose(false)}>Decline</Button>}
    {w.status === 'active' && <div className="flex gap-2">
      <Button size="sm" variant="secondary" disabled={busy} onClick={() => onClose(false)}>End now</Button>
      <Button size="sm" variant="secondary" disabled={busy} onClick={() => onClose(true)}>End and stop running jobs</Button>
    </div>}
  </li>;
}
